import {
  handleDropInAnotherRow,
  handleDropInTheSameRow,
  resetLayout,
} from '@modules/dashboard/components/Layout/DashboardCell/helpers';
import { MAX_CELLS_PER_ROW } from '@modules/dashboard/constants';
import {
  getAutoScrollParams,
  getDashboardRow,
  startOrContinueAutoscroll,
  stopAutoscroll,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { DashboardState } from '@modules/dashboard/store/types';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { cloneDeep, throttle } from 'lodash';
import { useMemo } from 'react';

interface useCellDropEventHandlersParams {
  rowID: string;
  cellID: string;
}

export function useCellDropEventHandlers({
  cellID,
  rowID,
}: useCellDropEventHandlersParams) {
  const { upsertDashboard } = useUpsertDashboard();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const rows = useDashboardStore(({ metadata }) => metadata.layout.rows);
  const dragItemData = useDashboardStore(({ visual }) => visual.dragItemData);
  const dropTargetData = useDashboardStore(
    ({ visual }) => visual.dropTargetData,
  );
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );

  const persistDropTargetData = useMemo(
    () =>
      throttle(
        (newDropTargetData: DashboardState['visual']['dropTargetData']) => {
          dispatch({
            type: DashboardActionType.SET_VISUAL,
            payload: {
              dropTargetData: newDropTargetData,
            },
          });
        },
        200,
      ),
    [dispatch],
  );

  function onDragOver(eve: React.DragEvent) {
    eve.preventDefault();

    const scrollParams = getAutoScrollParams(eve.clientY);

    if (scrollParams) {
      startOrContinueAutoscroll({ ...scrollParams, speed: 600 });
    } else {
      stopAutoscroll();
    }

    const newDropTargetData = { cellID, rowID };
    const dropTarget = eve.currentTarget as HTMLElement;
    const isDropTargetInTheSameRow =
      newDropTargetData.rowID === dragItemData?.rowID;
    const row = getDashboardRow({ rowID: newDropTargetData.rowID, rows });

    if (!row) {
      eve.dataTransfer.dropEffect = 'none';
      persistDropTargetData(null);
      return;
    }

    const hasTargetRowSpace = row.cells.length < MAX_CELLS_PER_ROW;
    const canAddCell = isDropTargetInTheSameRow ? true : hasTargetRowSpace;

    if (!canAddCell) {
      eve.dataTransfer.dropEffect = 'none';
      persistDropTargetData(null);
      return;
    }

    const rect = dropTarget.getBoundingClientRect();
    const isLeftDrop = eve.clientX - (rect.left + rect.width / 2) < 0;

    eve.dataTransfer.dropEffect = 'move';

    persistDropTargetData({
      cellID: newDropTargetData.cellID,
      rowID: newDropTargetData.rowID,
      isLeftDrop,
    });
  }

  function onDrop(eve: React.DragEvent) {
    eve.preventDefault();
    stopAutoscroll();

    if (!dropTargetData || !dragItemData) {
      return;
    }

    const { rowID: sourceRowId, cellID: draggedCellID } = dragItemData;
    const {
      rowID: destinationRowId,
      cellID: dropTargetCellID,
      isLeftDrop,
    } = dropTargetData;

    if (draggedCellID === dropTargetCellID) {
      return;
    }

    const updatedRows = cloneDeep(rows);
    const sourceRow = getDashboardRow({
      rowID: sourceRowId,
      rows: updatedRows,
    });
    const destinationRow = getDashboardRow({
      rowID: destinationRowId,
      rows: updatedRows,
    });
    const sourceRowIdx = updatedRows.findIndex((row) => row.id === sourceRowId);
    const destinationRowIdx = updatedRows.findIndex(
      (row) => row.id === destinationRowId,
    );

    const sourceRowCells = sourceRow?.cells ?? [];
    const destinationRowCells = destinationRow?.cells ?? [];

    const draggedCell = sourceRowCells.find(
      (item) => item.id === draggedCellID,
    );
    const draggedCellIdx = sourceRowCells.findIndex(
      (item) => item.id === draggedCellID,
    );

    if (!draggedCell) {
      return;
    }

    const dropTargetCellIdx = destinationRowCells.findIndex(
      (item) => item.id === dropTargetCellID,
    );
    const dropTargetIdx = dropTargetCellIdx + (isLeftDrop ? 0 : 1);

    const clonedCellMinimumHeightsByRowID = cloneDeep(
      cellMinimumHeightsByRowID,
    );

    if (sourceRowId === destinationRowId) {
      handleDropInTheSameRow({
        dropTargetIdx,
        draggedCell,
        destinationRowCells,
      });
    } else {
      handleDropInAnotherRow({
        dropTargetIdx,
        draggedCell,
        sourceRowCells,
        destinationRowCells,
      });

      const height =
        clonedCellMinimumHeightsByRowID[sourceRowId][draggedCellID];
      delete clonedCellMinimumHeightsByRowID[sourceRowId][draggedCellID];
      clonedCellMinimumHeightsByRowID[destinationRowId][draggedCellID] = height;

      resetLayout({ rows: updatedRows });
    }

    const layout = { rows: updatedRows };

    const isSameRow = sourceRowIdx === destinationRowIdx;
    const isDraggedFromLeft = draggedCellIdx < dropTargetCellIdx;
    const isDraggedFromTop = sourceRowIdx < destinationRowIdx;
    const from = isSameRow
      ? isDraggedFromLeft
        ? 'left'
        : 'right'
      : isDraggedFromTop
      ? 'top'
      : 'bottom';
    const to = isSameRow
      ? isDraggedFromLeft
        ? 'right'
        : 'left'
      : isDraggedFromTop
      ? 'bottom'
      : 'top';

    trackDashboardEvent({
      eventName: 'Dashboard Widget Reorder',
      from,
      to,
    });

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: {
        layout,
      },
    });

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        dragItemData: null,
        dropTargetData: null,
        cellMinimumHeightsByRowID: clonedCellMinimumHeightsByRowID,
      },
    });

    upsertDashboard({
      dashboardProps: {
        metadataV2: {
          layout,
        },
      },
    });
  }

  return { onDragOver, onDrop };
}
